/**
 * @typedef {Object} PositionNode
 * @property {HTMLElement} parent - Parent d'origine de l'élément
 * @property {HTMLElement} element - L'élément à déplacer
 * @property {HTMLElement} to - Destination
 * @property {string} breakpointName - Nom du breakpoint (ex: 'mobile')
 * @property {string} query - Media query calculée
 * @property {string|number} order - Position dans la destination
 * @property {number} index - Index d'origine dans le parent
 */

/**
 * Script de positionnement dynamique d'éléments selon les breakpoints
 * Utilise des media queries "between" (plages exclusives)
 * 
 * @param {Object} breakpoints - Objet définissant les breakpoints {nom: 'valeurpx'}
 * 
 * @example
 * // Définir les breakpoints
 * usePosition({ mobile: '640px', tablette: '1024px', desktop: '1280px' });
 * 
 * // Plages générées automatiquement :
 * // mobile   : 0 à 639px        → (max-width: 639px)
 * // tablette : 640px à 1023px   → (min-width: 640px) and (max-width: 1023px)
 * // desktop  : 1024px à 1279px  → (min-width: 1024px) and (max-width: 1279px)
 * // > 1280px : position d'origine
 * 
 * // Dans le HTML
 * <div data-position=".menu-mobile,mobile;.menu-tablette,tablette,2">...</div>
 */
export function usePosition(breakpoints = {}) {
  const className = '_position_adapted_'
  const attrName = 'data-position'

  // Convertir et trier les breakpoints par valeur croissante
  const sortedBreakpoints = Object.entries(breakpoints)
    .map(([name, value]) => ({ name, value: parseInt(value, 10) }))
    .sort((a, b) => a.value - b.value)

  // Créer les plages (ranges) pour chaque breakpoint
  // Ex: mobile: 640 → plage 0-639
  //     tablette: 1024 → plage 640-1023
  const breakpointRanges = {}
  sortedBreakpoints.forEach((bp, index) => {
    const minValue = index === 0 ? 0 : sortedBreakpoints[index - 1].value
    const maxValue = bp.value - 1

    // Construire la media query "between"
    let query
    if (minValue === 0) {
      // Premier breakpoint : seulement max-width
      query = `(max-width: ${maxValue}px)`
    } else {
      // Breakpoints suivants : min-width AND max-width
      query = `(min-width: ${minValue}px) and (max-width: ${maxValue}px)`
    }

    breakpointRanges[bp.name] = {
      min: minValue,
      max: maxValue,
      query
    }
  })

  /** @type {PositionNode[]} */
  const nodes = getNodes()

  if (nodes.length === 0) return

  // Grouper les nodes par élément pour gérer les configs multiples
  const elementGroups = new Map()
  nodes.forEach((node) => {
    if (!elementGroups.has(node.element)) {
      elementGroups.set(node.element, [])
    }
    elementGroups.get(node.element).push(node)
  })

  // Collecter toutes les media queries uniques
  const uniqueQueries = [...new Set(nodes.map(n => n.query))]

  // Fonction pour recalculer la position de tous les éléments
  function updateAllElements() {
    elementGroups.forEach((groupNodes, element) => {
      // Trouver le node dont la media query match
      let activeNode = null

      for (const node of groupNodes) {
        if (window.matchMedia(node.query).matches) {
          activeNode = node
          break // Une seule plage peut matcher à la fois
        }
      }

      if (activeNode) {
        // Vérifier si l'élément est déjà dans le bon parent ET à la bonne position
        const isInCorrectParent = element.parentElement === activeNode.to
        const isAtCorrectPosition = isInCorrectParent && checkPosition(element, activeNode.to, activeNode.order)
        const isAlreadyInPlace = isInCorrectParent && isAtCorrectPosition

        if (!isAlreadyInPlace) {
          // D'abord remettre à la position d'origine si déjà déplacé
          if (element.classList.contains(className)) {
            const originalNode = groupNodes.find(n => n.index >= 0) || groupNodes[0]
            moveBack(originalNode)
          }
          // Puis déplacer vers la nouvelle destination
          moveTo(activeNode)
        }
      } else {
        // Aucun breakpoint actif, remettre à la position d'origine
        if (element.classList.contains(className)) {
          const originalNode = groupNodes.find(n => n.index >= 0) || groupNodes[0]
          moveBack(originalNode)
        }
      }
    })
  }

  // Créer les media queries et écouter les changements
  uniqueQueries.forEach((query) => {
    const matchMedia = window.matchMedia(query)
    matchMedia.addEventListener('change', updateAllElements)
  })

  // Écouter le resize de la fenêtre avec debounce
  let resizeTimeout = null
  window.addEventListener('resize', () => {
    if (resizeTimeout) {
      clearTimeout(resizeTimeout)
    }
    resizeTimeout = setTimeout(updateAllElements, 100)
  })

  // Exécuter une première fois
  updateAllElements()

  /**
   * Récupère tous les éléments avec data-position et parse leurs configurations
   * @returns {PositionNode[]}
   */
  function getNodes() {
    const result = []
    const elements = [...document.querySelectorAll(`[${attrName}]`)]

    elements.forEach((element) => {
      const attr = element.getAttribute(attrName)
      // Supporter plusieurs configurations séparées par ";"
      const configs = attr.split(';').map((c) => c.trim()).filter(Boolean)
      console.log(attr)
      configs.forEach((config) => {
        const [toSelector, breakpointName, order] = config.split(',').map((val) => val.trim())

        // Vérifier que le breakpoint existe
        if (!breakpointRanges[breakpointName]) {
          console.warn(`[usePosition] Breakpoint "${breakpointName}" non défini. Ignoré.`)
          return
        }

        const to = document.querySelector(toSelector)

        if (!to) {
          console.warn(`[usePosition] Sélecteur "${toSelector}" non trouvé. Ignoré.`)
          return
        }

        result.push({
          parent: element.parentElement,
          element,
          to,
          breakpointName,
          query: breakpointRanges[breakpointName].query,
          order: order !== undefined ? (isNumber(order) ? Number(order) : order) : 'last',
          index: -1,
        })
      })
    })

    return result
  }

  /**
   * Déplace un élément vers sa destination
   * @param {PositionNode} node
   */
  function moveTo(node) {
    const { to, element, order } = node
    // Sauvegarder l'index d'origine avant de déplacer
    node.index = getIndexInParent(element, element.parentElement)
    element.classList.add(className)

    if (order === 'last' || order >= to.children.length) {
      to.append(element)
      return
    }

    if (order === 'first') {
      to.prepend(element)
      return
    }

    if (typeof order === 'number' && to.children[order]) {
      to.children[order].before(element)
    } else {
      to.append(element)
    }
  }

  /**
   * Remet un élément à sa position d'origine
   * @param {PositionNode} node
   */
  function moveBack(node) {
    const { parent, element, index } = node
    element.classList.remove(className)

    if (index >= 0 && parent.children[index]) {
      parent.children[index].before(element)
    } else {
      parent.append(element)
    }
  }

  /**
   * Récupère l'index d'un élément dans son parent
   * @param {HTMLElement} element
   * @param {HTMLElement} parent
   * @returns {number}
   */
  function getIndexInParent(element, parent) {
    return [...parent.children].indexOf(element)
  }

  /**
   * Vérifie si une valeur est un nombre
   * @param {*} value
   * @returns {boolean}
   */
  function isNumber(value) {
    return !isNaN(value) && value !== ''
  }

  /**
   * Vérifie si un élément est à la bonne position dans son parent
   * @param {HTMLElement} element
   * @param {HTMLElement} parent
   * @param {string|number} order
   * @returns {boolean}
   */
  function checkPosition(element, parent, order) {
    const currentIndex = getIndexInParent(element, parent)
    const childCount = parent.children.length

    if (order === 'first') {
      return currentIndex === 0
    }

    if (order === 'last') {
      return currentIndex === childCount - 1
    }

    if (typeof order === 'number') {
      // Si l'order demandé dépasse le nombre d'enfants, l'élément devrait être à la fin
      if (order >= childCount) {
        return currentIndex === childCount - 1
      }
      return currentIndex === order
    }

    return true
  }

  // Retourner une fonction pour forcer la mise à jour si nécessaire
  return {
    update: updateAllElements,
    nodes,
    ranges: breakpointRanges,
  }
}
